import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase/config';
import { deleteUser, GoogleAuthProvider, OAuthProvider, reauthenticateWithPopup, EmailAuthProvider, reauthenticateWithCredential } from 'firebase/auth';
import { auth } from '../firebase/config';
import { StorageService } from './storageService';

/**
 * 계정 삭제 서비스
 * 사용자의 모든 데이터를 삭제하고 Firebase Auth 계정도 삭제
 */
export class AccountService {
  /**
   * 최근 로그인 필요 시 재인증을 수행
   * Google/Apple은 팝업으로, 이메일/비밀번호는 상위 UI에서 비밀번호를 받아 처리해야 하므로
   * 비밀번호 계정의 경우 에러를 던져 상위에서 안내하도록 한다
   */
  static async reauthenticateIfNeeded(): Promise<void> {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error('No authenticated user');
    }

    const lastSignInTime = currentUser.metadata.lastSignInTime;
    const needsRecentLogin = lastSignInTime
      ? (Date.now() - new Date(lastSignInTime).getTime()) > (24 * 60 * 60 * 1000)
      : true;

    if (!needsRecentLogin) return;

    const providerId = currentUser.providerData?.[0]?.providerId;
    if (providerId === 'google.com') {
      await reauthenticateWithPopup(currentUser, new GoogleAuthProvider());
      return;
    }
    if (providerId === 'apple.com') {
      await reauthenticateWithPopup(currentUser, new OAuthProvider('apple.com'));
      return;
    }

    // 이메일/비밀번호 계정은 상위에서 비밀번호를 받아 credential 재인증 필요
    // 상위에서 처리하도록 신호를 보냄
    throw new Error('reauth-password-required');
  }
  /**
   * 계정 삭제 실행
   * 1. 재인증 확인 및 수행 (필요시)
   * 2. Cloud Function을 통해 서버에서 데이터 삭제
   * 3. 클라이언트에서 Firebase Auth 계정 삭제
   */
  static async deleteAccount(): Promise<{ success: boolean; message: string; deletedData?: any }> {
    try {
      console.log('🗑️ [AccountService] Starting account deletion...');
      
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error('No authenticated user found');
      }

      // Step 1: 재인증 필요 여부 확인 및 수행
      console.log('🔐 [AccountService] Checking if reauthentication is needed...');
      try {
        // 최근 로그인 확인 (24시간 이내)
        const lastSignInTime = currentUser.metadata.lastSignInTime;
        const needsRecentLogin = lastSignInTime
          ? (Date.now() - new Date(lastSignInTime).getTime()) > (24 * 60 * 60 * 1000)
          : false;

        if (needsRecentLogin) {
          console.log('🔄 [AccountService] Recent login required, performing reauthentication...');
          const providerId = currentUser.providerData?.[0]?.providerId;
          
          if (providerId === 'google.com') {
            await reauthenticateWithPopup(currentUser, new GoogleAuthProvider());
            console.log('✅ [AccountService] Google reauthentication successful');
          } else if (providerId === 'apple.com') {
            await reauthenticateWithPopup(currentUser, new OAuthProvider('apple.com'));
            console.log('✅ [AccountService] Apple reauthentication successful');
          } else {
            // 이메일/비밀번호 계정은 재인증이 필요하지만 여기서는 이미 로그인한 상태이므로 계속 진행
            console.log('ℹ️ [AccountService] Email/password account, skipping reauthentication');
          }
        } else {
          console.log('✅ [AccountService] Recent login confirmed, no reauthentication needed');
        }
      } catch (reauthError: any) {
        console.error('❌ [AccountService] Reauthentication failed:', reauthError);
        if (reauthError.code === 'auth/user-mismatch' || reauthError.code === 'auth/user-not-found') {
          throw new Error('requires-recent-login');
        }
        throw reauthError;
      }

      // Step 2: 사용자 상태를 최신으로 동기화
      console.log('🔁 [AccountService] Reloading user state...');
      try {
        await currentUser.reload();
        console.log('✅ [AccountService] User state reloaded');
      } catch (reloadError) {
        console.warn('⚠️ [AccountService] Failed to reload user:', reloadError);
      }

      // Step 3: 토큰을 강제로 새로고침하여 인증 컨텍스트를 보장
      console.log('🔄 [AccountService] Refreshing ID token...');
      let idToken: string;
      try {
        // 여러 번 시도하여 토큰을 확실히 갱신
        idToken = await currentUser.getIdToken(true);
        // 토큰이 확실히 갱신되도록 짧은 지연
        await new Promise(resolve => setTimeout(resolve, 100));
        idToken = await currentUser.getIdToken(true);
        console.log('✅ [AccountService] ID token refreshed successfully, length:', idToken?.length || 0);
      } catch (tokenError: any) {
        console.error('❌ [AccountService] Failed to refresh ID token:', tokenError);
        if (tokenError.code === 'auth/requires-recent-login') {
          throw new Error('requires-recent-login');
        }
        throw new Error(`Failed to refresh authentication token: ${tokenError.message}`);
      }

      // Step 4: Cloud Function을 통해 서버에서 모든 데이터 삭제
      console.log('☁️ [AccountService] Calling Cloud Function to delete server data...');
      const deleteUserAccount = httpsCallable(functions, 'deleteUserAccount');
      
      // 최신 토큰을 callable 데이터로도 전달하여 서버에서 직접 검증 가능하도록 함
      // httpsCallable은 자동으로 인증 토큰을 전달하지만, 추가적으로 data에도 포함하여 이중 검증
      const result = await deleteUserAccount({ authToken: idToken });
      
      console.log('✅ [AccountService] Server data deletion result:', result.data);
      
      // Step 5: 클라이언트에서 Firebase Auth 계정 삭제
      console.log('🔐 [AccountService] Deleting Firebase Auth account...');
      try {
        await deleteUser(currentUser);
        console.log('✅ [AccountService] Firebase Auth account deleted');
      } catch (deleteError: any) {
        // 이미 삭제된 경우 성공으로 처리
        if (deleteError.code === 'auth/user-not-found') {
          console.log('ℹ️ [AccountService] User already deleted from Firebase Auth');
        } else {
          throw deleteError;
        }
      }
      
      console.log('🎉 [AccountService] Account deletion completed successfully');
      
      return {
        success: true,
        message: 'Account and all data deleted successfully',
        deletedData: result.data
      };

    } catch (error: any) {
      console.error('❌ [AccountService] Account deletion failed:', error);
      
      // Firebase Auth 삭제 실패 시 특별 처리
      if (error.code === 'auth/user-not-found') {
        console.log('ℹ️ [AccountService] User already deleted from Firebase Auth');
        return {
          success: true,
          message: 'Account data deleted (Auth account was already deleted)',
          warning: 'Firebase Auth account was already deleted'
        };
      }
      
      // 함수 호출 시 인증 누락을 최근 로그인 필요 케이스로 매핑
      if (
        error?.code === 'functions/unauthenticated' ||
        error?.code === 'unauthenticated' ||
        error?.message?.includes('User must be authenticated') ||
        error?.message?.includes('requires-recent-login')
      ) {
        throw new Error('requires-recent-login');
      }
      
      throw new Error(`Account deletion failed: ${error.message}`);
    }
  }

  /**
   * 계정 삭제 전 데이터 백업 (선택사항)
   * 사용자가 중요한 데이터를 다운로드할 수 있도록 도움
   */
  static async exportUserData(): Promise<{ success: boolean; data?: any }> {
    try {
      console.log('📤 [AccountService] Exporting user data...');
      
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error('No authenticated user found');
      }

      // TODO: 사용자 데이터 내보내기 기능 구현
      // - 카드 데이터 JSON으로 내보내기
      // - 청구서 데이터 내보내기
      // - 프로필 정보 내보내기
      
      return {
        success: true,
        data: {
          message: 'Data export feature will be implemented soon',
          userId: currentUser.uid,
          email: currentUser.email
        }
      };

    } catch (error: any) {
      console.error('❌ [AccountService] Data export failed:', error);
      throw new Error(`Data export failed: ${error.message}`);
    }
  }

  /**
   * 계정 삭제 가능 여부 확인
   * 최근 로그인한 사용자만 삭제 가능하도록 제한
   */
  static canDeleteAccount(): { canDelete: boolean; reason?: string } {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        return { canDelete: false, reason: 'No authenticated user' };
      }

      // 최근 로그인 확인 (24시간 이내)
      const lastSignInTime = currentUser.metadata.lastSignInTime;
      if (lastSignInTime) {
        const lastSignIn = new Date(lastSignInTime);
        const now = new Date();
        const hoursSinceLastSignIn = (now.getTime() - lastSignIn.getTime()) / (1000 * 60 * 60);
        
        if (hoursSinceLastSignIn > 24) {
          return { 
            canDelete: false, 
            reason: 'Recent login required for security. Please sign out and sign in again.' 
          };
        }
      }

      return { canDelete: true };

    } catch (error) {
      console.error('❌ [AccountService] Account deletion check failed:', error);
      return { canDelete: false, reason: 'Unable to verify account status' };
    }
  }
}



